import { getAdvertisingId, getTrackingPermissionsAsync } from 'expo-tracking-transparency';

import logger from '../logger';

/**
 * Returns the advertising identifier for the current device:
 * - iOS: IDFA (requires ATT permission via expo-tracking-transparency)
 * - Android: GAID (always resolves as granted, no explicit permission prompt needed)
 *
 * Uses `getTrackingPermissionsAsync()` as the semantic guard instead of relying
 * on hard-coded values (e.g. the zeroed IDFA string):
 * - iOS 14+: reflects the user's ATT choice ('granted' | 'denied' | 'undetermined')
 * - iOS <14 / Android / web: always resolves to 'granted'
 * - iOS Simulator: resolves to 'undetermined', so advertising ID is not fetched
 *
 * Returns `undefined` in all cases where tracking is not allowed or a valid ID
 * is unavailable, to avoid sending null/zeroed values to Amplitude → Snowflake → HighTouch.
 */
export const getAdDeviceId = async (): Promise<string | undefined> => {
  try {
    const { status } = await getTrackingPermissionsAsync();

    if (status !== 'granted') {
      logger.info(`Ad tracking not allowed (status: ${status}). Skipping advertising ID fetch.`);
      return undefined;
    }

    const advertisingId = getAdvertisingId();

    if (!advertisingId) {
      return undefined;
    }

    return advertisingId;
  } catch (error) {
    logger.warn({ message: 'Error fetching advertising ID', error });
    return undefined;
  }
};
